import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  WhatsApp,
  Facebook,
  Instagram,
  Send,
  Dashboard,
} from "@material-ui/icons";
import ConecthubEmbedFlowModal from "../ConecthubEmbedFlowModal";
import ConecthubMetaEmbedFlowModal from "../ConecthubMetaEmbedFlowModal";
import { i18n } from "../../translate/i18n";

/** Desligado até liberarmos o fluxo Telegram no ConectHUB. */
const TELEGRAM_CONECTHUB_UI_ENABLED = false;

const useStyles = makeStyles((theme) => ({
  paper: {
    backgroundColor: "#1a1d29",
    color: "#fff",
  },
  title: {
    fontWeight: 700,
    color: "#fff",
  },
  subtitle: {
    color: "rgba(255,255,255,0.65)",
    marginTop: theme.spacing(0.5),
  },
  card: {
    backgroundColor: "#252936",
    borderRadius: 12,
    padding: theme.spacing(2),
    textAlign: "center",
    cursor: "pointer",
    border: "1px solid transparent",
    transition: "transform 0.15s ease, border-color 0.15s ease",
    minHeight: 120,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    "&:hover": {
      transform: "scale(1.02)",
      borderColor: "rgba(124, 58, 237, 0.5)",
    },
  },
  cardDisabled: {
    position: "relative",
    opacity: 0.45,
    cursor: "not-allowed",
    border: "1px solid rgba(255, 193, 7, 0.45)",
    "&:hover": {
      transform: "none",
    },
  },
  cardLabel: {
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  hubBadge: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: theme.spacing(2),
    color: "#a78bfa",
    fontSize: "0.85rem",
  },
}));

/**
 * Entrada ConectHUB 360: escolha de canal → fluxo existente (WA Oficial) ou embed Meta (FB/IG).
 */
function Conecthub360FlowModal({
  open,
  onClose,
  onCompleted,
  planWhatsapp,
  planWhatsappOfficial,
  planFacebook,
  planInstagram,
  planConecthub360,
}) {
  const classes = useStyles();
  const [step, setStep] = useState("picker");

  const waHubEnabled = Boolean(planWhatsapp && planWhatsappOfficial);
  const fbEnabled = Boolean(planFacebook);
  const igEnabled = Boolean(planInstagram);
  const tgHubEnabled =
    TELEGRAM_CONECTHUB_UI_ENABLED && Boolean(planConecthub360);

  useEffect(() => {
    if (open) setStep("picker");
  }, [open]);

  const handleCloseAll = () => {
    setStep("picker");
    onClose();
  };

  const pickOrToast = (enabled, next, msgKey) => {
    if (!enabled) {
      toast.warning(i18n.t(msgKey));
      return;
    }
    setStep(next);
  };

  if (!open) {
    return null;
  }

  if (step === "whatsapp") {
    return (
      <ConecthubEmbedFlowModal
        open
        onBack={() => setStep("picker")}
        onClose={handleCloseAll}
        onCompleted={onCompleted}
      />
    );
  }

  if (step === "facebook") {
    return (
      <ConecthubMetaEmbedFlowModal
        open
        channel="facebook"
        onBack={() => setStep("picker")}
        onClose={handleCloseAll}
        onCompleted={onCompleted}
      />
    );
  }

  if (step === "instagram") {
    return (
      <ConecthubMetaEmbedFlowModal
        open
        channel="instagram"
        onBack={() => setStep("picker")}
        onClose={handleCloseAll}
        onCompleted={onCompleted}
      />
    );
  }

  if (step === "telegram") {
    return (
      <ConecthubMetaEmbedFlowModal
        open
        channel="telegram"
        onBack={() => setStep("picker")}
        onClose={handleCloseAll}
        onCompleted={onCompleted}
      />
    );
  }

  return (
    <Dialog
      open
      onClose={handleCloseAll}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle disableTypography>
        <Box className={classes.hubBadge}>
          <Dashboard style={{ color: "#7c3aed", fontSize: 22 }} />
          <span>ConectHUB 360</span>
        </Box>
        <Typography variant="h6" className={classes.title}>
          {i18n.t("conecthub360Picker.title")}
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          {i18n.t("conecthub360Picker.subtitle")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={6}>
            <Box
              className={`${classes.card} ${!waHubEnabled ? classes.cardDisabled : ""}`}
              onClick={() =>
                pickOrToast(
                  waHubEnabled,
                  "whatsapp",
                  "conecthub360Picker.planNoWhatsappOfficial"
                )
              }
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  pickOrToast(
                    waHubEnabled,
                    "whatsapp",
                    "conecthub360Picker.planNoWhatsappOfficial"
                  );
              }}
            >
              <WhatsApp style={{ fontSize: 40, color: "#25d366" }} />
              <span className={classes.cardLabel}>
                {i18n.t("conecthub360Picker.whatsappOfficial")}
              </span>
            </Box>
          </Grid>
          <Grid item xs={6} sm={6}>
            <Box
              className={`${classes.card} ${!fbEnabled ? classes.cardDisabled : ""}`}
              onClick={() =>
                pickOrToast(fbEnabled, "facebook", "conecthub360Picker.planNoFacebook")
              }
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  pickOrToast(fbEnabled, "facebook", "conecthub360Picker.planNoFacebook");
              }}
            >
              <Facebook style={{ fontSize: 40, color: "#3b5998" }} />
              <span className={classes.cardLabel}>
                {i18n.t("conecthub360Picker.facebook")}
              </span>
            </Box>
          </Grid>
          <Grid item xs={6} sm={6}>
            <Box
              className={`${classes.card} ${!igEnabled ? classes.cardDisabled : ""}`}
              onClick={() =>
                pickOrToast(
                  igEnabled,
                  "instagram",
                  "conecthub360Picker.planNoInstagram"
                )
              }
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  pickOrToast(
                    igEnabled,
                    "instagram",
                    "conecthub360Picker.planNoInstagram"
                  );
              }}
            >
              <Instagram style={{ fontSize: 40, color: "#e1306c" }} />
              <span className={classes.cardLabel}>
                {i18n.t("conecthub360Picker.instagram")}
              </span>
            </Box>
          </Grid>
          <Grid item xs={6} sm={6}>
            <Box
              className={`${classes.card} ${!tgHubEnabled ? classes.cardDisabled : ""}`}
              onClick={() =>
                pickOrToast(
                  tgHubEnabled,
                  "telegram",
                  TELEGRAM_CONECTHUB_UI_ENABLED
                    ? "conecthub360Picker.planNoConecthub360"
                    : "conecthub360Picker.telegramDisabled"
                )
              }
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  pickOrToast(
                    tgHubEnabled,
                    "telegram",
                    TELEGRAM_CONECTHUB_UI_ENABLED
                      ? "conecthub360Picker.planNoConecthub360"
                      : "conecthub360Picker.telegramDisabled"
                  );
              }}
            >
              <Send style={{ fontSize: 40, color: "#229ED9" }} />
              <span className={classes.cardLabel}>
                {i18n.t("conecthub360Picker.telegram")}
              </span>
              {!TELEGRAM_CONECTHUB_UI_ENABLED && (
                <Typography
                  variant="caption"
                  style={{ color: "rgba(255,193,7,0.95)", marginTop: 4 }}
                >
                  {i18n.t("conecthub360Picker.comingSoon")}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: 16 }}>
        <Button onClick={handleCloseAll} style={{ color: "rgba(255,255,255,0.8)" }}>
          {i18n.t("conecthubEmbed.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Conecthub360FlowModal;
